import { TrendingUp, Bell } from "lucide-react";
import ToggleSwitch from "../ui/ToggleSwitch";
import { PALETTE } from "../../styles/configStyles";
import EffectiveDateBadge from "../EffectiveDateBadge";

const OvertimeTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  config,
  setConfig,
  canEditField,
  getFieldStyle,
  isCreatingNewConfig,
  isEditingArchive,
  unlockTab,
  resetTabToGlobal,
  getTabEffectiveDate,
  getBadgeMode,
}) => {
  const canEdit = canEditField("overtime");

  const getOvertimeValue = (field) => {
    if (
      config.department &&
      config.departmentRules &&
      typeof config.departmentRules === "object" &&
      config.departmentRules !== null &&
      Object.prototype.hasOwnProperty.call(config.departmentRules, config.department) &&
      config.departmentRules[config.department]
    ) {
      const deptRules = config.departmentRules[config.department];
      return deptRules && deptRules[field] !== undefined ? deptRules[field] : config[field];
    }
    return config[field];
  };

  const handleOvertimeChange = (field, value) => {
    if (config.department) {
      setConfig((prev) => ({
        ...prev,
        departmentRules: {
          ...prev.departmentRules,
          [prev.department]: {
            ...(prev.departmentRules && prev.departmentRules[prev.department] ? prev.departmentRules[prev.department] : {}),
            [field]: value,
            overtimeEffectiveDate: prev.overtimeEffectiveDate || config.overtimeEffectiveDate,
          },
        },
      }));
    } else {
      // Company-wide change: update top-level value and mirror to all department rules
      setConfig((prev) => {
        const newDeptRules = {};
        if (prev.departmentRules && typeof prev.departmentRules === "object") {
          Object.keys(prev.departmentRules).forEach((dept) => {
            newDeptRules[dept] = {
              ...(prev.departmentRules[dept] || {}),
              [field]: value,
              overtimeEffectiveDate: prev.overtimeEffectiveDate || config.overtimeEffectiveDate,
            };
          });
        }

        return {
          ...prev,
          [field]: value,
          departmentRules: Object.keys(newDeptRules).length ? { ...prev.departmentRules, ...newDeptRules } : prev.departmentRules,
        };
      });
    }
  };

  return (
    <>
      <div style={{ marginTop: "24px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
            <div style={styles.sectionIconBadge(PALETTE.amber.base)}>
              <TrendingUp size={20} />
            </div>
            {t.overtime.title}
            {!canEdit && (
              <span style={{
                fontSize: "11px",
                fontWeight: "700",
                marginLeft: isRTL ? "0" : "12px",
                marginRight: isRTL ? "12px" : "0",
                padding: "4px 12px",
                borderRadius: "6px",
                background: `${PALETTE.amber.base}15`,
                color: PALETTE.amber.base,
                border: `1px solid ${PALETTE.amber.base}25`,
              }}>
                {language === "ar" ? "(للقراءة فقط)" : "(Read-only)"}
              </span>
            )}
          </h3>
          <EffectiveDateBadge
            date={getTabEffectiveDate ? getTabEffectiveDate("overtime") : config.overtimeEffectiveDate}
            isRTL={isRTL}
            theme={theme}
            mode={getBadgeMode ? getBadgeMode("overtime") : "locked"}
            onUnlock={unlockTab ? () => unlockTab("overtime") : undefined}
            onReset={resetTabToGlobal ? () => resetTabToGlobal("overtime") : undefined}
            onDateChange={(date) => setConfig((prev) => ({ ...prev, overtimeEffectiveDate: date }))}
          />
        </div>

        <div style={styles.toggleContainer}>
          <label style={styles.label}>{t.overtime.enableOvertime}</label>
          <ToggleSwitch
            checked={getOvertimeValue("overtimeEnabled")}
            onChange={(e) =>
              canEdit && handleOvertimeChange("overtimeEnabled", e.target.checked)
            }
            disabled={!canEdit}
            theme={theme}
            accentColor={PALETTE.amber.base}
            isRTL={isRTL}
          />
        </div>

        {getOvertimeValue("overtimeEnabled") && (
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.overtime.overtimeRate}</label>
              <input
                type="number"
                min="0"
                step="any"
                value={getOvertimeValue("overtimeRate") ?? ""}
                onChange={(e) => {
                  if (!canEdit) return;

                  const value = e.target.value;

                  handleOvertimeChange(
                    "overtimeRate",
                    value === "" ? null : Number(value)
                  );
                }}
                disabled={!canEdit}
                style={getFieldStyle("overtime", styles.input)}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.overtime.overtimeStartsAfter}</label>
              <input
                type="number"
                min="0"
                max="24"
                value={getOvertimeValue("overtimeStartAfter") ?? ""}
                onChange={(e) => {
                  if (!canEdit) return;

                  const value = e.target.value;

                  handleOvertimeChange(
                    "overtimeStartAfter",
                    value === "" ? null : Number(value)
                  );
                }}
                disabled={!canEdit}
                style={getFieldStyle("overtime", styles.input)}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.overtime.maxOvertimePerDay}</label>
              <input
                type="number"
                min="0"
                max="24"
                value={getOvertimeValue("maxOvertimeHoursPerDay") ?? ""}
                onChange={(e) => {
                  if (!canEdit) return;
                  const value = e.target.value;
                  handleOvertimeChange(
                    "maxOvertimeHoursPerDay",
                    value === "" ? null : parseInt(value, 10)
                  );
                }}
                disabled={!canEdit}
                style={getFieldStyle("overtime", styles.input)}
              />
            </div>
          </div>
        )}

        <div style={styles.toggleContainer}>
          <label style={styles.label}>{t.overtime.approvalRequired}</label>
          <ToggleSwitch
            checked={getOvertimeValue("overtimeApprovalRequired")}
            onChange={(e) =>
              canEdit && handleOvertimeChange("overtimeApprovalRequired", e.target.checked)
            }
            disabled={!canEdit}
            theme={theme}
            accentColor={PALETTE.amber.base}
            isRTL={isRTL}
          />
        </div>
      </div>
    </>
  );
};

export default OvertimeTab;